import React from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';

import { INotification } from '@models';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@store';

import { AuthenticationStatus } from '@store/types';

import { Loader } from '@components';

import Header from '../containers/Header';
import Navigation from '../containers/Navigation';
import ContactUs from '../containers/ContactUs';
import Footer from '../containers/Footer';
import RoutesSwitch from '../containers/RoutesSwitch';
import AccountPickerHeader from '../containers/AccountPickerHeader';
import CookiePolicyPlaceholder from '../containers/CookiePolicyPlaceholder';

import NotificationModalDialog from '../components/notification/NotificationModalDialog';
import { ErrorStateType } from '@models';

import { useTheme, useAccountsService, useIsMounted } from '@hooks';

import '@styles/main.scss';

export interface AppStateProps {
  authenticationStatus: AuthenticationStatus;
  accountId?: string;
  adaptiveSuperUser: boolean;
  initialPayloadFetched: boolean;
  notifications: INotification[];
}

export interface AppDispatchProps {
  getOrders: () => void;
  getAccounts: () => void;
  setInitialPayloadFetched: () => void;
  getBuilds: () => void;
  removeNotification: () => void;
  getAccountInfo: () => void;
  getClinicalReport: () => void;
}

const App: React.FC<AppStateProps & AppDispatchProps> = ({
  authenticationStatus,
  getOrders,
  adaptiveSuperUser,
  getBuilds,
  notifications,
  removeNotification,
  getClinicalReport,
}) => {
  useTheme();
  const { start } = useAccountsService();
  const history = useHistory();
  const [containerStyling, setContainerStyling] =
    React.useState<string>('main-container');

  const isMounted = useIsMounted();

  const [isTosError, setIsTOSError] = React.useState(false);

  // AccountId - @Store --> ProjectPicker --> AccountId
  const isActiveAccountId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeAccountId
  );

  const isActiveProjectId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeProjectId
  );

  const hasValidTOS = useSelector(
    (state: IApplicationState) => state.authentication.user?.hasValidTOS
  );

  const isErrors = useSelector(
    (state: IApplicationState) => state.error.errors
  );

  React.useEffect(() => {
    if (adaptiveSuperUser) {
      setContainerStyling('main-container-admin');
    }
  }, [adaptiveSuperUser]);

  // we need to make sure the app is mounted first before updating
  // or else pages with :tokens will route back
  React.useEffect(() => {
    const resetURL = history.location.pathname.split('/')[1];

    if (!isMounted) {
      history.push(`/${resetURL}`);
    }
  }, [isActiveAccountId, isActiveProjectId]);

  // IF ERRORS ARE DETECTED
  // CHECK IF THERE IS A TOS ERROR
  React.useEffect(() => {
    const hasTosError = isErrors.includes(ErrorStateType.InvalidTOS);
    if (hasTosError) {
      setIsTOSError(true);
    } else {
      setIsTOSError(false);
    }
  }, [isErrors]);

  React.useEffect(() => {
    if (
      (authenticationStatus === AuthenticationStatus.authenticated &&
        !isTosError) ||
      (authenticationStatus === AuthenticationStatus.authenticated &&
        hasValidTOS)
    ) {
      const startProjectPicker = async () => {
        await start();
      };
      startProjectPicker();
    }
  }, [AuthenticationStatus, isTosError, hasValidTOS, isTosError]);

  React.useEffect(() => {
    // bootstrap application data on successful log in
    if (
      isActiveProjectId &&
      authenticationStatus === AuthenticationStatus.authenticated &&
      !isTosError
    ) {
      getClinicalReport();
      getOrders();
      getBuilds();
    }
  }, [isActiveAccountId, isActiveProjectId, authenticationStatus, isTosError]);

  const notification = _.first(notifications);

  return (
    <div className='app'>
      <CookiePolicyPlaceholder />

      <AccountPickerHeader />
      <Header />

      <div className={containerStyling}>
        <Navigation />
        {authenticationStatus === AuthenticationStatus.none && <Loader />}
        {authenticationStatus !== AuthenticationStatus.none && <RoutesSwitch />}
      </div>
      <Footer />
      <ContactUs />
      <NotificationModalDialog
        show={!!notification}
        title={notification?.title || ''}
        dismiss={removeNotification}
      >
        {notification?.body || ''}
      </NotificationModalDialog>
    </div>
  );
};

export default App;
